// LeaderboardsPage — Phase 5.2.3 (operator visibility)
//
// Three loyalty rankings powering AI promotions and ops decisions:
//   - Top Spenders   : lifetime $ on delivered orders
//   - Top Earners    : lifetime EARN points (excludes admin grants/transfers)
//   - Top Referrers  : converted referrals (referee placed ≥1 delivered order)
//
// Visibility:
//   - super_admin: can switch region (DMV / NJ / All)
//   - regional dispatcher: locked to their own region (the backend ignores
//     the region param for non-super-admin, but we hide the picker too)

import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Trophy, DollarSign, Coins, Users as UsersIcon,
  RefreshCw, MapPin, Star, ChevronDown, ChevronRight, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const METRICS = [
  {
    key: 'spenders',
    label: 'TOP SPENDERS',
    icon: DollarSign,
    accent: '#39FF14',
    primaryField: 'lifetime_total',
    primaryLabel: 'LIFETIME $',
    formatPrimary: (v) => `$${(v ?? 0).toFixed(2)}`,
    secondaryField: 'order_count',
    secondaryLabel: 'ORDERS',
    formatSecondary: (v) => `${v} order${v === 1 ? '' : 's'}`,
    timestampField: 'last_order_at',
    timestampLabel: 'LAST ORDER',
  },
  {
    key: 'earners',
    label: 'TOP EARNERS',
    icon: Coins,
    accent: '#00F0FF',
    primaryField: 'earned_total',
    primaryLabel: 'PTS EARNED',
    formatPrimary: (v) => `${v ?? 0} pts`,
    secondaryField: 'earn_events',
    secondaryLabel: 'EVENTS',
    formatSecondary: (v) => `${v} event${v === 1 ? '' : 's'}`,
    timestampField: 'last_earned_at',
    timestampLabel: 'LAST EARN',
  },
  {
    key: 'referrers',
    label: 'TOP REFERRERS',
    icon: UsersIcon,
    accent: '#FF9F0A',
    primaryField: 'converted_referrals',
    primaryLabel: 'CONVERTED',
    formatPrimary: (v) => `${v ?? 0}`,
    secondaryField: 'referral_pts_earned',
    secondaryLabel: 'REF PTS',
    formatSecondary: (v) => `${v ?? 0} pts`,
    timestampField: 'last_referral_at',
    timestampLabel: 'LAST REFERRAL',
  },
  {
    // Feb 2026 (Track B) — reviewers ranked by approved review count,
    // then likes received as a tiebreak. Points earned come from ledger
    // sources 'review' and 'review_like'.
    key: 'reviewers',
    label: 'TOP REVIEWERS',
    icon: Star,
    accent: '#FF3B30',
    primaryField: 'review_count',
    primaryLabel: 'REVIEWS',
    formatPrimary: (v) => `${v ?? 0}`,
    secondaryField: 'likes_received',
    secondaryLabel: 'LIKES',
    formatSecondary: (v) => `${v ?? 0} like${v === 1 ? '' : 's'}`,
    timestampField: 'last_review_at',
    timestampLabel: 'LAST REVIEW',
  },
];

const SINCE_OPTIONS = [
  { value: 'all', label: 'ALL TIME' },
  { value: '90', label: 'LAST 90D' },
  { value: '30', label: 'LAST 30D' },
];

const LIMIT_OPTIONS = [10, 25, 50];

function relativeAgo(iso) {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return '1d ago';
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return months === 1 ? '1mo ago' : `${months}mo ago`;
  } catch {
    return '—';
  }
}

export function LeaderboardsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [metric, setMetric] = useState(() => {
    const t = searchParams.get('tab');
    return METRICS.find((m) => m.key === t) ? t : 'spenders';
  });
  const [region, setRegion] = useState('all'); // super-admin only
  const [since, setSince] = useState('all');
  const [limit, setLimit] = useState(25);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  // Feb 2026 — row-expand state: only ONE row at a time. Persist to ?expanded=
  const [expandedUserId, setExpandedUserId] = useState(searchParams.get('expanded') || null);
  const [historyByUser, setHistoryByUser] = useState({}); // {user_id: {loading, data, error}}

  // Keep URL params in lockstep with UI state so links are shareable
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (metric === 'spenders') next.delete('tab');
    else next.set('tab', metric);
    if (expandedUserId) next.set('expanded', expandedUserId);
    else next.delete('expanded');
    setSearchParams(next, { replace: true });
  }, [metric, expandedUserId, searchParams, setSearchParams]);

  // Feb 2026 (post-QA fix v2) — Tab switching must collapse the expansion
  // (history is metric-scoped). Doing this imperatively in the click handler
  // instead of a metric-change useEffect avoids the React.StrictMode double-
  // mount pitfall that stripped ?expanded= from URLs on hard reload.
  const changeMetric = useCallback((newMetric) => {
    if (newMetric === metric) return;
    setMetric(newMetric);
    setExpandedUserId(null);
  }, [metric]);

  // load regions list for the picker (super-admin only)
  useEffect(() => {
    if (!isSuperAdmin) return;
    axios
      .get(`${API_URL}/api/regions`, { withCredentials: true })
      .then(({ data }) => setRegions(data?.regions || []))
      .catch(() => {});
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { metric, since, limit };
      if (isSuperAdmin && region !== 'all') params.region = region;
      const { data } = await axios.get(`${API_URL}/api/loyalty/leaderboard`, {
        params,
        withCredentials: true,
      });
      setRows(data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load leaderboard');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [metric, region, since, limit, isSuperAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // Feb 2026 — Row expansion: fetch history on demand, cache per (user, metric)
  const loadHistory = useCallback(async (userId, metricKey) => {
    const cacheKey = `${userId}::${metricKey}`;
    setHistoryByUser((p) => {
      if (p[cacheKey]?.data) return p; // already loaded
      return { ...p, [cacheKey]: { loading: true } };
    });
    try {
      const { data } = await axios.get(`${API_URL}/api/loyalty/leaderboard/history`, {
        params: { user_id: userId, metric: metricKey, limit: 10 },
        withCredentials: true,
      });
      setHistoryByUser((p) => ({ ...p, [cacheKey]: { loading: false, data } }));
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load history';
      toast.error(msg);
      setHistoryByUser((p) => ({ ...p, [cacheKey]: { loading: false, error: msg } }));
    }
  }, []);

  const toggleRow = useCallback(async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    await loadHistory(userId, metric);
  }, [expandedUserId, metric, loadHistory]);

  // Auto-fetch history for a URL-derived expansion on mount (deep-link support).
  const initialExpandedRef = useRef(searchParams.get('expanded'));
  useEffect(() => {
    if (initialExpandedRef.current) {
      loadHistory(initialExpandedRef.current, metric);
      initialExpandedRef.current = null; // one-shot
    }
  }, [loadHistory, metric]);

  const activeMetric = METRICS.find((m) => m.key === metric) || METRICS[0];
  const Icon = activeMetric.icon;

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="leaderboards-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dispatch"
            className="inline-flex items-center text-[#4B6B52] hover:text-[#39FF14] font-mono text-sm mb-2"
            data-testid="leaderboards-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> BACK TO DISPATCH
          </Link>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-[#39FF14]" />
            <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
              {'>'} LOYALTY_LEADERBOARDS
            </h1>
          </div>
          <p className="text-[#4B6B52] font-mono text-sm mt-1">
            // OPERATOR VISIBILITY — feeds Smart Promotions targeting. Customers never see this.
          </p>
        </div>

        {/* Metric tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="metric-tabs">
          {METRICS.map((m) => {
            const MIcon = m.icon;
            const active = m.key === metric;
            return (
              <button
                key={m.key}
                onClick={() => changeMetric(m.key)}
                data-testid={`metric-tab-${m.key}`}
                className={`flex items-center gap-2 px-4 py-2 border font-mono text-xs uppercase tracking-wider transition-all ${
                  active
                    ? 'bg-[#0C0612] font-bold'
                    : 'bg-transparent border-[#2A0F3D] text-[#4B6B52] hover:text-[#A3C7A8]'
                }`}
                style={active ? { borderColor: m.accent, color: m.accent } : {}}
              >
                <MIcon className="w-3.5 h-3.5" />
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        <div
          className="bg-[#0C0612] border border-[#2A0F3D] p-4 mb-6 flex flex-wrap items-end gap-4"
          data-testid="filter-bar"
        >
          {isSuperAdmin && (
            <div>
              <p className="text-[10px] text-[#4B6B52] font-mono uppercase mb-1">REGION</p>
              <div className="flex gap-1" data-testid="region-filter">
                <FilterPill
                  active={region === 'all'}
                  onClick={() => setRegion('all')}
                  testid="region-pill-all"
                >
                  ALL
                </FilterPill>
                {regions.map((r) => (
                  <FilterPill
                    key={r.code}
                    active={region === r.code}
                    onClick={() => setRegion(r.code)}
                    testid={`region-pill-${r.code}`}
                  >
                    {r.code.toUpperCase()}
                  </FilterPill>
                ))}
              </div>
            </div>
          )}
          {!isSuperAdmin && (
            <div
              className="text-[#4B6B52] font-mono text-xs flex items-center gap-1"
              data-testid="region-locked-notice"
            >
              <MapPin className="w-3 h-3" />
              {(user?.region || '').toUpperCase()} ONLY
            </div>
          )}

          <div>
            <p className="text-[10px] text-[#4B6B52] font-mono uppercase mb-1">TIME WINDOW</p>
            <div className="flex gap-1" data-testid="since-filter">
              {SINCE_OPTIONS.map((opt) => (
                <FilterPill
                  key={opt.value}
                  active={since === opt.value}
                  onClick={() => setSince(opt.value)}
                  testid={`since-pill-${opt.value}`}
                >
                  {opt.label}
                </FilterPill>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] text-[#4B6B52] font-mono uppercase mb-1">LIMIT</p>
            <div className="flex gap-1" data-testid="limit-filter">
              {LIMIT_OPTIONS.map((n) => (
                <FilterPill
                  key={n}
                  active={limit === n}
                  onClick={() => setLimit(n)}
                  testid={`limit-pill-${n}`}
                >
                  TOP {n}
                </FilterPill>
              ))}
            </div>
          </div>

          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="refresh-leaderboard"
            className="ml-auto border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] rounded-none font-mono"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> REFRESH
          </Button>
        </div>

        {/* Results */}
        <div
          className="bg-[#0C0612] border p-4"
          style={{ borderColor: activeMetric.accent + '40' }}
          data-testid="leaderboard-results"
        >
          <div className="flex items-center gap-2 mb-4">
            <Icon className="w-4 h-4" style={{ color: activeMetric.accent }} />
            <p
              className="font-mono text-xs uppercase tracking-wider font-bold"
              style={{ color: activeMetric.accent }}
            >
              // {activeMetric.label} — {since === 'all' ? 'ALL TIME' : `LAST ${since}D`}
              {isSuperAdmin && region !== 'all' && ` · ${region.toUpperCase()}`}
            </p>
          </div>

          {loading ? (
            <p className="text-[#4B6B52] font-mono text-sm py-8 text-center">// loading...</p>
          ) : rows.length === 0 ? (
            <div
              className="text-[#4B6B52] font-mono text-sm py-8 text-center"
              data-testid="leaderboard-empty"
            >
              // no data yet — once customers start ordering, they&apos;ll show up here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-xs">
                <thead>
                  <tr className="border-b border-[#2A0F3D] text-[10px] text-[#4B6B52] uppercase">
                    <th className="text-left py-2 px-2 w-6"></th>
                    <th className="text-left py-2 px-2 w-10">#</th>
                    <th className="text-left py-2 px-2">CUSTOMER</th>
                    <th className="text-left py-2 px-2 w-20">REGION</th>
                    <th className="text-right py-2 px-2">{activeMetric.primaryLabel}</th>
                    {metric === 'referrers' && (
                      <th className="text-right py-2 px-2 text-[#4B6B52]" data-testid="header-pending">
                        PENDING
                      </th>
                    )}
                    <th className="text-right py-2 px-2 hidden md:table-cell">
                      {activeMetric.secondaryLabel}
                    </th>
                    <th className="text-right py-2 px-2 hidden sm:table-cell">
                      {activeMetric.timestampLabel}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const isExpanded = expandedUserId === row.user_id;
                    const cacheKey = `${row.user_id}::${metric}`;
                    const hist = historyByUser[cacheKey];
                    return (
                      <Fragment key={row.user_id}>
                        <tr
                          className="border-b border-[#1a0625] hover:bg-[rgba(57,255,20,0.03)] cursor-pointer"
                          data-testid={`leaderboard-row-${row.user_id}`}
                          onClick={() => toggleRow(row.user_id)}
                        >
                          <td className="py-2.5 px-2 text-[#4B6B52]">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleRow(row.user_id); }}
                              data-testid={`leaderboard-row-expand-${row.user_id}`}
                              className="hover:text-[#39FF14]"
                              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                            >
                              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                          </td>
                          <td className="py-2.5 px-2 text-[#4B6B52]">
                            {idx === 0 ? (
                              <span style={{ color: activeMetric.accent }}>🏆</span>
                            ) : (
                              <>{idx + 1}</>
                            )}
                          </td>
                          <td className="py-2.5 px-2">
                            {/* Phase 5.9.29 — click name to open User Management drawer */}
                            <Link
                              to={`/dispatch/admin/users?openUser=${row.user_id}&from=leaderboards`}
                              onClick={(e) => e.stopPropagation()}
                              className="block hover:underline"
                              data-testid={`leaderboard-name-link-${row.user_id}`}
                            >
                              <div className="text-[#A3C7A8]">{row.name}</div>
                            </Link>
                          </td>
                          <td className="py-2.5 px-2">
                            {row.region ? (
                              <span className="inline-block px-1.5 py-0.5 border border-[#2A0F3D] text-[10px] text-[#6B8E73] uppercase">
                                {row.region}
                              </span>
                            ) : (
                              <span className="text-[#4B6B52]">—</span>
                            )}
                          </td>
                          <td
                            className="py-2.5 px-2 text-right font-bold"
                            style={{ color: activeMetric.accent }}
                          >
                            {activeMetric.formatPrimary(row[activeMetric.primaryField])}
                          </td>
                          {metric === 'referrers' && (
                            <td
                              className="py-2.5 px-2 text-right text-[#6B8E73]"
                              data-testid={`pending-count-${row.user_id}`}
                            >
                              {row.pending_referrals ?? 0}
                            </td>
                          )}
                          <td className="py-2.5 px-2 text-right text-[#A3C7A8] hidden md:table-cell">
                            {activeMetric.formatSecondary(row[activeMetric.secondaryField])}
                          </td>
                          <td className="py-2.5 px-2 text-right text-[#4B6B52] hidden sm:table-cell">
                            {relativeAgo(row[activeMetric.timestampField])}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${row.user_id}-detail`} data-testid={`leaderboard-row-detail-${row.user_id}`}>
                            <td colSpan={metric === 'referrers' ? 8 : 7} className="p-0">
                              <div
                                className="border-b border-[#2A0F3D] px-4 py-3 bg-[#050003]"
                                style={{ borderLeft: `2px solid ${activeMetric.accent}` }}
                              >
                                <HistoryPanel
                                  metric={metric}
                                  accent={activeMetric.accent}
                                  loading={hist?.loading}
                                  error={hist?.error}
                                  data={hist?.data}
                                  userId={row.user_id}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-[10px] text-[#4B6B52] font-mono mt-4">
          // Emails are masked. To see full details for a customer, use{' '}
          {isSuperAdmin ? (
            <Link to="/dispatch/admin/points" className="text-[#39FF14] hover:underline">
              POINTS_ADMIN
            </Link>
          ) : (
            <span>POINTS_ADMIN (super-admin only)</span>
          )}
          .
        </p>
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-wider transition-all ${
        active
          ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.06)]'
          : 'border-[#2A0F3D] text-[#4B6B52] hover:text-[#A3C7A8] hover:border-[#4B6B52]'
      }`}
    >
      {children}
    </button>
  );
}


// -----------------------------------------------------------------------------
// HistoryPanel — Feb 2026 (Track B leaderboards drilldown)
//
// Renders the expanded-row payload from GET /api/loyalty/leaderboard/history.
// The columns and totals differ per metric because the underlying event stream
// is metric-specific (orders for spenders, ledger entries for earners, referral
// conversions for referrers, reviews for reviewers).
// -----------------------------------------------------------------------------
function HistoryPanel({ metric, accent, loading, error, data, userId }) {
  if (loading) {
    return (
      <p className="text-[#4B6B52] font-mono text-xs py-2" data-testid={`history-loading-${userId}`}>
        // loading history…
      </p>
    );
  }
  if (error) {
    return (
      <p className="text-[#FF3B30] font-mono text-xs py-2" data-testid={`history-error-${userId}`}>
        // {error}
      </p>
    );
  }
  if (!data || (!data.history?.length && !data.pending?.length)) {
    return (
      <div className="flex items-center justify-between">
        <p className="text-[#4B6B52] font-mono text-xs py-1" data-testid={`history-empty-${userId}`}>
          // no {metric} events for this user yet.
        </p>
        <Link
          to={`/dispatch/admin/users?openUser=${userId}&from=leaderboards`}
          className="text-[10px] font-mono uppercase hover:underline"
          style={{ color: accent }}
          data-testid={`history-open-user-${userId}`}
        >
          OPEN USER <ExternalLink className="inline w-3 h-3 ml-0.5" />
        </Link>
      </div>
    );
  }

  const t = data.totals || {};
  const showAllLink = (
    <Link
      to={`/dispatch/admin/users?openUser=${userId}&from=leaderboards`}
      className="text-[10px] font-mono uppercase hover:underline"
      style={{ color: accent }}
      data-testid={`history-open-user-${userId}`}
    >
      SEE ALL → OPEN USER <ExternalLink className="inline w-3 h-3 ml-0.5" />
    </Link>
  );

  return (
    <div className="space-y-2" data-testid={`history-panel-${userId}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[10px] font-mono uppercase" style={{ color: accent }}>
          // {data.user?.name} — {metric.toUpperCase()} HISTORY (last {data.limit})
        </p>
        {showAllLink}
      </div>

      {/* Totals strip */}
      <div className="flex flex-wrap gap-4 text-[10px] font-mono">
        {Object.entries(totalsForMetric(metric, t)).map(([label, val]) => (
          <div key={label}>
            <span className="text-[#4B6B52] uppercase">{label}: </span>
            <span className="text-[#A3C7A8] font-bold">{val}</span>
          </div>
        ))}
      </div>

      {/* Metric-specific history table (converted / delivered activity) */}
      {data.history.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full font-mono text-[11px]" data-testid={`history-table-${userId}`}>
            <thead>
              <tr className="border-b border-[#1a0625] text-[10px] text-[#4B6B52] uppercase">
                {columnsForMetric(metric).map((c) => (
                  <th key={c.key} className={`py-1.5 px-2 text-${c.align || 'left'}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.history.map((h, idx) => (
                <tr key={idx} data-testid={`history-row-${userId}-${idx}`} className="border-b border-[#1a0625]/50">
                  {columnsForMetric(metric).map((c) => (
                    <td key={c.key} className={`py-1.5 px-2 text-${c.align || 'left'} ${c.className || 'text-[#A3C7A8]'}`}>
                      {c.render ? c.render(h) : h[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Feb 2026 (post-QA v5) — PENDING section for referrers only.
          Lists invitees who signed up but haven't placed a delivered order yet. */}
      {metric === 'referrers' && data.pending && data.pending.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#2A0F3D]" data-testid={`pending-section-${userId}`}>
          <p className="text-[10px] font-mono uppercase mb-1.5 text-[#6B8E73]">
            // PENDING ({data.pending.length}) — signed up, no delivered orders yet
          </p>
          <table className="w-full font-mono text-[11px]">
            <thead>
              <tr className="border-b border-[#1a0625] text-[10px] text-[#4B6B52] uppercase">
                <th className="py-1.5 px-2 text-left">SIGNED UP</th>
                <th className="py-1.5 px-2 text-left">REFERRED</th>
                <th className="py-1.5 px-2 text-left">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {data.pending.map((p, idx) => (
                <tr key={p.user_id} data-testid={`pending-row-${userId}-${idx}`} className="border-b border-[#1a0625]/50">
                  <td className="py-1.5 px-2 text-[#A3C7A8]">{relativeAgo(p.signed_up_at)}</td>
                  <td className="py-1.5 px-2">
                    {p.name ? (
                      <Link
                        to={`/dispatch/admin/users?openUser=${p.user_id}&from=leaderboards`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#A3C7A8] hover:text-[#39FF14] hover:underline"
                        data-testid={`pending-referred-link-${p.user_id}`}
                      >
                        {p.name}
                      </Link>
                    ) : (
                      <span className="text-[#6B8E73]" data-testid={`pending-referred-masked-${p.user_id}`}>
                        {p.email_masked || '—'}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-[#FFB020]">No delivered orders yet</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function totalsForMetric(metric, t) {
  if (metric === 'spenders') {
    return {
      Orders: t.order_count ?? 0,
      Spent: `$${Number(t.lifetime_total || 0).toFixed(2)}`,
    };
  }
  if (metric === 'earners') {
    return {
      Events: t.earn_events ?? 0,
      Earned: `${t.earned_total ?? 0} pts`,
    };
  }
  if (metric === 'referrers') {
    return {
      Converted: t.converted_referrals ?? 0,
      Pending: t.pending_referrals ?? 0,
      'Ref pts': `${t.referral_pts_earned ?? 0} pts`,
    };
  }
  if (metric === 'reviewers') {
    return {
      Reviews: t.review_count ?? 0,
      Approved: t.approved_count ?? 0,
      Pending: t.pending_count ?? 0,
      'Avg rating': (t.avg_rating ?? 0).toFixed(2),
      Likes: t.likes_received ?? 0,
      'Pts earned': `${t.points_earned ?? 0} pts`,
    };
  }
  return {};
}

function columnsForMetric(metric) {
  if (metric === 'spenders') {
    return [
      { key: 'created_at', label: 'DATE', render: (h) => relativeAgo(h.created_at) },
      { key: 'order_id', label: 'ORDER',
        render: (h) => (
          <Link to={`/dispatch/orders/${h.order_id}`} className="text-[#39FF14] hover:underline">
            {(h.order_id || '').slice(0, 8).toUpperCase()}
          </Link>
        ) },
      { key: 'region', label: 'REGION',
        render: (h) => h.region ? h.region.toUpperCase() : '—' },
      { key: 'item_count', label: 'ITEMS', align: 'right' },
      { key: 'total', label: 'TOTAL', align: 'right',
        className: 'text-[#39FF14] font-bold',
        render: (h) => `$${Number(h.total || 0).toFixed(2)}` },
    ];
  }
  if (metric === 'earners') {
    return [
      { key: 'created_at', label: 'DATE', render: (h) => relativeAgo(h.created_at) },
      { key: 'source', label: 'SOURCE' },
      { key: 'reason', label: 'REASON',
        render: (h) => (h.reason || '').slice(0, 60) },
      { key: 'delta', label: 'PTS', align: 'right',
        className: 'text-[#00F0FF] font-bold',
        render: (h) => `+${h.delta ?? 0}` },
    ];
  }
  if (metric === 'referrers') {
    return [
      { key: 'created_at', label: 'DATE', render: (h) => relativeAgo(h.created_at) },
      { key: 'referee_email_masked', label: 'REFERRED',
        render: (h) => (
          h.referee_user_id && h.referee_name ? (
            <Link
              to={`/dispatch/admin/users?openUser=${h.referee_user_id}&from=leaderboards`}
              onClick={(e) => e.stopPropagation()}
              className="text-[#A3C7A8] hover:text-[#39FF14] hover:underline"
              data-testid={`referred-link-${h.referee_user_id}`}
            >
              {h.referee_name}
            </Link>
          ) : (
            <span className="text-[#6B8E73]" data-testid={`referred-masked-${h.referee_user_id || 'unknown'}`}>
              {h.referee_email_masked || '—'}
            </span>
          )
        ) },
      { key: 'referee_first_order_items', label: 'FIRST ORDER',
        render: (h) => h.referee_first_order_items || '—' },
      { key: 'referee_first_order_total', label: '1ST ORDER $', align: 'right',
        render: (h) => `$${Number(h.referee_first_order_total || 0).toFixed(2)}` },
      { key: 'referee_spent', label: 'LIFETIME $', align: 'right',
        render: (h) => `$${Number(h.referee_spent || 0).toFixed(2)}` },
      { key: 'pts_awarded', label: 'PTS', align: 'right',
        className: 'text-[#FF9F0A] font-bold',
        render: (h) => `+${h.pts_awarded ?? 0}` },
    ];
  }
  if (metric === 'reviewers') {
    return [
      { key: 'created_at', label: 'DATE', render: (h) => relativeAgo(h.created_at) },
      { key: 'product_name', label: 'PRODUCT' },
      { key: 'rating', label: 'RATING', align: 'right',
        render: (h) => '★'.repeat(h.rating || 0) + '☆'.repeat(Math.max(0, 5 - (h.rating || 0))) },
      { key: 'status', label: 'STATUS',
        render: (h) => (
          <span className={
            h.status === 'approved' ? 'text-[#39FF14]' :
            h.status === 'pending' ? 'text-[#FFB020]' :
            'text-[#FF3B30]'
          }>{(h.status || '').toUpperCase()}</span>
        ) },
      { key: 'like_count', label: 'LIKES', align: 'right' },
    ];
  }
  return [];
}

export default LeaderboardsPage;
